import React, { useCallback, useEffect, useState } from 'react';
import cn from 'clsx';
import toast from 'react-hot-toast';

import {
  CardElement,
  useStripe,
  useElements,
  Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { chargePledge } from '@sdk/managers/PaymentManager';
import { useHistory } from 'react-router-dom';

import ButtonMighty from '@components/elements/ButtonMighty';
import { useAuth } from '@hooks/Auth';
import { mutate } from 'swr';
import Dialog from '@components/elements/Dialog';
import InputText from '@components/elements/InputText';
import { Colors } from '@components/elements/Colors';
import { proxyCreateUser, updateUserByEmail } from '@sdk/managers/UsersManager';
import { useGlobalContext } from '@context/customContext';

interface PledgeCheckoutFormProps {
  pollId: string;
  userId?: string;
  proxy?: boolean;
  neon?: string;
}

const PledgeCheckoutForm: React.FC<PledgeCheckoutFormProps> = ({
  pollId,
  userId,
  proxy = false,
  neon,
}) => {
  const { dbUser, company, refetch } = useAuth();
  const history = useHistory<any>();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const [proxyModal, setProxyModal] = useState<boolean>(false);
  const hideProxyModal = useCallback(
    () => setProxyModal(false),
    [setProxyModal]
  );
  const showProxyModal = useCallback(
    () => setProxyModal(true),
    [setProxyModal]
  );

  const [userModal, setUserModal] = useState<boolean>(false);
  const hideUserModal = useCallback(() => setUserModal(false), [setUserModal]);
  const showUserModal = useCallback(() => setUserModal(true), [setUserModal]);
  const [email, setEmail] = useState<string>();
  const [coupon, setCoupon] = useState<string>('');
  const [phoneNo, setPhoneNo] = useState<string>();
  const [name, setName] = useState<string>();
  const { themeData } = useGlobalContext();

  let formErrors: Record<string, string> = {};
  if (name === undefined || name === '') formErrors.name = 'Empty name';
  if (email === undefined || email === '') formErrors.email = 'Empty email';

  useEffect(() => {
    if (dbUser?.id) mutate(['/poll/pledged', pollId, dbUser.id]);
  }, [dbUser, pollId]);

  const handleSubmit = async (event?: any) => {
    // Block native form submission.
    event?.preventDefault();
    console.log('submit');

    if (!dbUser) return history.push('/login');
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      console.log('Stripe not loaded yet');
      return;
    }

    setSubmitting(true);

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    // Use your card Element with other Stripe.js APIs
    if (cardElement) {
      const result = await stripe.createToken(cardElement);

      if (result.error) {
        console.log('[error]', result.error);
        toast.error(result.error.message || 'Failed to use card.');
      } else if (!result) {
        toast.error('Failed to use card.');
      } else {
        console.log('[token]', result.token);
        try {
          if (proxy) {
            if (!company) throw new Error('Failed to get company.');
            if (!email) throw new Error('Invalid email.');

            let chargeUserId = userId;
            console.log({ chargeUserId });
            if (!chargeUserId) {
              const proxyUser = await toast.promise(
                proxyCreateUser(dbUser.id, {
                  email: email,
                  username: email,
                  phone: phoneNo,
                  company: 'testcompany',
                  proxyUserID: dbUser.id,
                }) as Promise<{
                  data?: {
                    result: {
                      id: string;
                      owner: string;
                      username: string;
                    };
                  };
                }>,
                {
                  loading: 'Creating Proxy user...',
                  success: 'Created Proxy user',
                  error: (err) => {
                    console.log('Failed to create proxy user', err);
                    return (
                      err.error || err.message || 'Failed to create proxy user'
                    );
                  },
                }
              );

              chargeUserId = proxyUser.data?.result.id;
              if (!chargeUserId)
                throw new Error('Unexpected error creating proxy user');
            }

            const res = await chargePledge(
              chargeUserId,
              pollId,
              result.token.id,
              coupon
            );

            console.log(res);
            toast.success('Pledged for user');
            await mutate(['/poll/proxyVotes', pollId, dbUser.id]);
          } else {
            const res = await chargePledge(
              userId || dbUser.id,
              pollId,
              result.token.id,
              coupon
            );
            console.log(res);
            await mutate(['/poll/pledged', pollId, userId || dbUser.id]);
          }
        } catch (err: any) {
          console.log('failed to charge pledge', err);
          toast.error(err?.message || 'Failed to charge pledge.');
        } finally {
          setSubmitting(false);
          setPhoneNo(undefined);
          setEmail(undefined);
          setName(undefined);
        }
      }
    }

    setSubmitting(false);
  };

  const handleUserSubmit = async () => {
    if (!dbUser) return history.push('/login');
    setSubmitting(true);

    await toast.promise(
      updateUserByEmail(['id'], dbUser.email, {
        id: dbUser.id,
        phone: phoneNo,
      }),
      {
        loading: 'Updating phone number...',
        success: 'Updated phone number',
        error: 'Failed to update phone number',
      }
    );

    hideUserModal();

    await handleSubmit();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
      {proxy && (
        <Dialog
          className="text-center text-black bg-yellow"
          title="Proxy User"
          content={
            <div className="flex flex-col space-y-2">
              <p className="text-sm">Enter email here</p>
              <InputText
                name="email"
                type="email"
                placeholder="Email"
                onChange={(value) => setEmail(value)}
                value={email || ''}
                borderColor={
                  email !== undefined && formErrors.email
                    ? Colors.red
                    : undefined
                }
                shadowColor={
                  email !== undefined && formErrors.email
                    ? Colors.yellow
                    : undefined
                }
              />
              <p className="text-sm">Enter phone number here</p>
              <InputText
                name="phoneNo"
                type="phoneNo"
                placeholder="Phone Number"
                onChange={(value) => setPhoneNo(value)}
                value={phoneNo || ''}
                borderColor={
                  phoneNo !== undefined && formErrors.phoneNo
                    ? Colors.red
                    : undefined
                }
                shadowColor={
                  phoneNo !== undefined && formErrors.phoneNo
                    ? Colors.yellow
                    : undefined
                }
              />
              <p className="mb-4 text-sm">Enter name here</p>
              <InputText
                name="firstName"
                type="text"
                placeholder="Name"
                onChange={(value) => setName(value)}
                value={name || ''}
                borderColor={
                  name !== undefined && formErrors.name ? Colors.red : undefined
                }
                shadowColor={
                  name !== undefined && formErrors.name
                    ? Colors.yellow
                    : undefined
                }
              />
            </div>
          }
          open={proxyModal}
          onCancel={hideProxyModal}
          onConfirm={() => {
            hideProxyModal();
            handleSubmit();
          }}
        />
      )}
      <Dialog
        className="text-center text-black bg-yellow"
        title="Phone Number required"
        content={
          <div className="flex flex-col space-y-2">
            <p className="text-sm">Enter phone number here</p>
            <InputText
              name="phoneNo"
              type="phoneNo"
              placeholder="Phone Number"
              onChange={(value) => setPhoneNo(value)}
              value={phoneNo || ''}
              borderColor={
                phoneNo !== undefined && formErrors.phoneNo
                  ? Colors.red
                  : undefined
              }
              shadowColor={
                phoneNo !== undefined && formErrors.phoneNo
                  ? Colors.yellow
                  : undefined
              }
            />
          </div>
        }
        open={userModal}
        onCancel={hideUserModal}
        onConfirm={() => handleUserSubmit()}
      />

      <div
        style={{
          border: `2px solid ${neon}`,
          height: '52px',
          paddingTop: 15,
        }}
        className="px-2"
      >
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: '16px',
                color: 'white',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      <InputText
        value={coupon}
        onChange={(e) => {
          setCoupon(e);
        }}
        type="text"
        style={{
          marginTop: '32px',
          fontSize: '16px',
          color: '#424770',
          '::placeholder': {
            color: '#aab7c4',
          },
          background: 'black',
        }}
        placeholder="Promo code"
      />
      <div className={cn('w-full xl:w-auto pt-4', {})}>
        <ButtonMighty
          onClick={
            !proxy || userId
              ? dbUser?.phone === undefined
                ? showUserModal
                : () => {
                    handleSubmit();
                  }
              : showProxyModal
          }
          neon={neon}
          disabled={!stripe || isSubmitting}
          loading={isSubmitting}
          color={`${themeData.colors['poll-btnPledgeCastVote-color']}`}
          borderColor={`${themeData.colors['poll-btnPledgeCastVote-borderColor']}`}
          textColor={`${themeData.colors['poll-btnPledgeCastVote-textColor']}`}
          text={
            proxy ? (userId ? 'Pledge for user' : 'Pledge new user') : 'Pledge'
          }
          margin="0"
        />
      </div>
    </form>
  );
};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// if (process.env.REACT_APP_STRIPE_PUB_KEY === undefined)
//   throw new Error(
//     `Missing stripe publishable key ${process.env.REACT_APP_STRIPE_PUB_KEY}`
//   );
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUB_KEY ||
    'pk_test_51Iffk1IXx2HzzlEOIl2w2tJmhcSXOuitzV7SAkJaqi3aqRKOcOi2ElHYBR5YRT1ESJv4NsKvhTYBNSPnYEPcdQ6G00MG5sbPSM'
);

const WrappedPledgeCheckoutForm: React.FC<PledgeCheckoutFormProps> = (
  props
) => {
  return (
    <Elements stripe={stripePromise}>
      <PledgeCheckoutForm {...props} />
    </Elements>
  );
};

export default WrappedPledgeCheckoutForm;
